<template>
  <div>

    <div class="d-flex align-items-center justify-content-between mb-3">
      <strong class="pointer" @click="$emit('back')">
        <arrow-left-icon class="icon-sm"/> Grupos de permissão
      </strong>
      <span>
        <button v-if="group" class="btn btn-outline-secondary mr-2" @click="$emit('see-users')">
          {{ group.users_count || 0 }} usuário(s)
        </button>
        <template v-if="!readOnly">
          <button class="btn btn-outline-secondary mr-2" @click="$emit('back')">Cancelar</button>
          <button class="btn btn-primary" :disabled="saving" @click="save()">
            {{ group ? 'Salvar grupo' : 'Criar grupo' }}
          </button>
        </template>
      </span>
    </div>

    <div v-if="readOnly" class="alert alert-light border">
      <lock-icon class="icon-sm text-muted"/>
      Grupo de sistema. Não pode ser editado nem excluído: é o que garante que ninguém perca acesso
      na virada, e que
      <template v-if="group.system_key === 'account_admin'">toda permissão nova entre nele sozinha</template>
      <template v-else>o padrão de quem entra na conta continue valendo</template>.
      Para um conjunto diferente, duplique-o.
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <label class="small mb-1">Nome do grupo *</label>
        <input v-model="name" :disabled="readOnly" class="input form-control"
               placeholder="Ex.: Operação Frotas">
      </div>
      <div class="col-md-6">
        <label class="small mb-1">Descrição</label>
        <input v-model="description" :disabled="readOnly" class="input form-control"
               placeholder="Para que serve este grupo">
      </div>
    </div>

    <div class="border rounded px-3 py-2 mb-2 d-flex align-items-center justify-content-between">
      <small class="text-muted">
        <strong>{{ selected.length }}</strong> de {{ total }} permissões marcadas
        <template v-if="hidden > 0">
          · <strong>{{ hidden }}</strong> valor(es) oculto(s) no escopo
        </template>
        <span v-if="!group" class="d-block">As permissões padrão já vêm marcadas.</span>
      </small>
      <span v-if="!readOnly">
        <a class="small pointer" @click="markAll()">Marcar todas</a>
        <span class="text-muted mx-1">|</span>
        <a class="small text-muted pointer" @click="selected = []">Desmarcar todas</a>
      </span>
    </div>

    <permission-picker
        :all-companies="allCompanies"
        :catalog="catalog"
        :cnpjs="cnpjs"
        :disabled="readOnly"
        :scopes="scopes"
        :selected="selected"
        @all-companies="allCompanies = $event"
        @cnpjs="cnpjs = $event"
        @scope-clear="clearScope"
        @scope-toggle="toggleScope"
        @toggle="toggle"
        @toggle-many="toggleMany"/>

  </div>
</template>

<script>
  import GlobalLoading from '@/mixins/GlobalLoading'
  import PermissionPicker from './PermissionPicker'
  import { ArrowLeftIcon, LockIcon } from 'vue-feather-icons'
  import { allPermissionIds, defaultPermissionIds, withDependencies } from '@/models/permissionCatalog'
  import { hiddenCount } from '@/models/permissionsEffective'

  export default {
    components: { ArrowLeftIcon, LockIcon, PermissionPicker },
    computed: {
      hidden() {
        return hiddenCount(this.scopes)
      },
      readOnly() {
        return !!(this.group && this.group.system)
      },
      total() {
        return allPermissionIds(this.catalog.products).length
      }
    },
    created() {
      if (!this.group) return this.applyDefaults()

      this.allCompanies = this.group.all_companies
      this.cnpjs = [...(this.group.cnpjs || [])]
      this.description = this.group.description || ''
      this.name = this.group.name
      this.scopes = this.$parse.clone(this.group.scopes || {})
      this.selected = [...(this.group.permissions || [])]
    },
    data: () => ({
      allCompanies: true,
      cnpjs: [],
      description: '',
      name: '',
      saving: false,
      scopes: {},
      selected: []
    }),
    methods: {
      // Grupo novo nasce com o padrão do catálogo marcado. Só na criação e só
      // enquanto ninguém mexeu — editar grupo existente nunca passa por aqui.
      applyDefaults() {
        if (this.group || this.selected.length > 0) return

        this.selected = defaultPermissionIds(this.catalog.products)
      },
      clearScope(app, dimension) {
        this.writeScope(app, dimension, [])
      },
      markAll() {
        this.selected = allPermissionIds(this.catalog.products)
      },
      save() {
        const payload = {
          all_companies: this.allCompanies,
          cnpjs: this.cnpjs,
          description: this.description.trim(),
          name: this.name.trim(),
          permissions: this.selected,
          scopes: this.scopes
        }

        this.saving = true
        this.openGlobalLoad()

        const request = this.group
          ? this.axios.put(`permission_groups/${this.group.id}`, payload)
          : this.axios.post('permission_groups', payload)

        request
          .then(() => this.$notify.add(this.group ? 'Grupo atualizado' : 'Grupo criado'))
          .then(() => this.$emit('saved'))
          .catch(e => this.$notify.addError(e))
          .finally(() => {
            this.saving = false
            this.closeGlobalLoad()
          })
      },
      // Marcar uma ação traz o "Ver" do recurso; desmarcar o "Ver" leva as ações
      // embora. Mesma regra do PermissionCatalog#prerequisite_for no backend,
      // que fecha o conjunto para cima ao salvar.
      toggle(id, checked) {
        this.selected = withDependencies(this.catalog.products, this.selected, id, checked)
      },
      // "Tudo"/"Nada" de um nível passam pela mesma regra de dependência do
      // clique individual: marcar traz o "Ver" junto, desmarcar o "Ver" leva as
      // ações embora. Sem isso dava para ficar com "Baixar boleto" marcado e
      // "Ver listagem" desmarcado.
      toggleMany(ids, checked) {
        this.selected = ids.reduce(
          (current, id) => withDependencies(this.catalog.products, current, id, checked),
          this.selected
        )
      },
      toggleScope(app, dimension, value) {
        const current = (this.scopes[app] || {})[dimension] || []
        const next = current.includes(value)
          ? current.filter(item => item !== value)
          : [...current, value]

        this.writeScope(app, dimension, next)
      },
      // Grava (ou remove) os valores ocultos de uma dimensão sem deixar nó vazio.
      writeScope(app, dimension, values) {
        const scopes = this.$parse.clone(this.scopes)
        const perApp = Object.assign({}, scopes[app] || {})

        if (values.length === 0) delete perApp[dimension]
        else perApp[dimension] = values

        if (Object.keys(perApp).length === 0) delete scopes[app]
        else scopes[app] = perApp

        this.scopes = scopes
      }
    },
    mixins: [GlobalLoading],
    name: 'PermissionGroupForm',
    props: {
      catalog: { required: true },
      group: { default: null }
    },
    // O catálogo chega por request: se ainda não tinha chegado no created,
    // marca o padrão quando chegar.
    watch: {
      catalog: 'applyDefaults'
    }
  }
</script>

<style scoped>
  .pointer { cursor: pointer; }
  .icon-sm { width: 14px; height: 14px; }
</style>
